import { useRef, useState } from 'react';

import FormikTextFieldV2 from '@perpay-web/components/base/TextFieldV2/FormikTextFieldV2';
import { getClassName } from '@perpay-web/utils/domUtils';
import { KeyboardKey } from '@perpay-web/utils/keyboardUtils';
import { noop } from '@perpay-web/utils/noop';

import styles from './Combobox.scss';

/**
 * Implements a WAI-ARIA compliant combo-box widget with manual selection.
 * https://www.w3.org/TR/wai-aria-practices/examples/combobox/aria1.1pattern/listbox-combo.html
 */
const ComboboxV2 = ({
    children,
    expanded,
    getValueAtIndex,
    highlightedIndex = null,
    id,
    inputValue,
    label,
    loading,
    name,
    onArrowDown,
    onArrowUp,
    onFocus,
    onBlur,
    onEscape,
    onHighlightIndex,
    onInputValueChange,
    onSelect,
    placeholder,
    selectedItem = null,
    ...rest
}) => {
    const renderFunc = children;
    const inputRef = useRef();

    return (
        <div className={styles.combobox}>
            <FormikTextFieldV2
                onFocus={onFocus}
                onBlur={onBlur}
                ref={inputRef}
                label={label}
                type='text'
                loading={loading}
                field={{
                    'aria-activedescendant':
                        typeof highlightedIndex === 'number'
                            ? `${id}-option-${highlightedIndex}`
                            : null,
                    'aria-autocomplete': 'list',
                    'aria-controls': `${id}-listbox`,
                    'aria-expanded': expanded,
                    'aria-haspopup': 'listbox',
                    'aria-live': 'polite',
                    'aria-owns': `${id}-listbox`,
                    name,
                    onChange: (e) => {
                        onInputValueChange(e.target.value);
                    },
                    onKeyDown: (event) => {
                        switch (event.key) {
                            case KeyboardKey.ARROW_DOWN:
                                onArrowDown(highlightedIndex);
                                break;
                            case KeyboardKey.ARROW_UP:
                                onArrowUp(highlightedIndex);
                                break;
                            case KeyboardKey.ESCAPE:
                                onEscape();
                                break;
                            case KeyboardKey.ENTER: {
                                if (
                                    highlightedIndex !== null &&
                                    highlightedIndex !== undefined
                                ) {
                                    const highlightedItem =
                                        getValueAtIndex(highlightedIndex);
                                    onSelect(
                                        highlightedItem && highlightedItem.text,
                                    );
                                }
                                break;
                            }
                            default:
                                break;
                        }
                    },
                    placeholder,
                    role: 'combobox',
                    value: inputValue,
                }}
                {...(rest.form ? { form: rest.form } : {})}
            />
            <div
                className={styles.combobox__results}
                hidden={!expanded}
                role='listbox'
                id={`${id}-listbox`}
            >
                {renderFunc({
                    onHighlightIndex,
                    onSelect,
                    highlightedIndex,
                    selectedItem,
                    getItemId: (index) => `${id}-option-${index}`,
                    getHighlightedCss: () =>
                        styles['combobox__result--highlighted'],
                    getSelectedCss: () => styles['combobox__result--selected'],
                    getResultCss: () => styles.combobox__result,
                })}
            </div>
        </div>
    );
};

/**
 * Returns a React Component that renders the combobox results  and is bound to
 * the given items array. DefaultComboboxResults renders the results list with
 * sane defaults so that consumers of Combobox do not need to implement the results
 * themselves for a simple array of items. e.g.
 * ```
 * <Combobox ...>
 * {defaultRender(results)}
 * </Combobox>
 * ```
 *
 * For more complex results data, a custom
 * component should be created by the consumer of Combobox and passed as the
 * children prop, e.g.
 * ```
 * <Combobox ...>
 * {({
 *      getHighlightedCss,
 *      getItemId,
 *      getResultCss,
 *      getSelectedCss,
 *      highlightedIndex,
 *      onHighlightIndex,
 *      onSelect,
 *      selectedItem,
 * }) => (
 *      <ul>complexResults.map(...)</ul>
 * )}
 * </Combobox>
 * ```
 * @knipignore Exported for testing only
 */
export function defaultRender(items) {
    const DefaultComboboxResults = ({
        getHighlightedCss,
        getItemId,
        getResultCss,
        getSelectedCss,
        highlightedIndex = null,
        onHighlightIndex,
        onSelect,
        selectedItem = null,
    }) => (
        <ul>
            {items.map((item, index) => (
                <li
                    id={getItemId(index)}
                    key={item.text}
                    className={
                        getResultCss() +
                        (highlightedIndex === index
                            ? ` ${getHighlightedCss()}`
                            : '') +
                        (selectedItem === item.text
                            ? ` ${getSelectedCss()}`
                            : '')
                    }
                    role='option'
                    aria-selected={
                        item ? item.value === highlightedIndex : null
                    }
                >
                    <button
                        type='button'
                        className={getClassName(
                            styles.combobox__dropdown__button,
                        )}
                        onMouseOver={() => onHighlightIndex(index)}
                        onClick={() => {
                            onSelect(item.text);
                        }}
                        onFocus={() => {
                            onHighlightIndex(index);
                        }}
                    >
                        {item.text}
                    </button>
                </li>
            ))}
        </ul>
    );

    return DefaultComboboxResults;
}

const StatefulComboboxV2 = ({
    value,
    onInputValueChange = noop,
    onFocus = noop,
    onBlur = noop,
    itemCount,
    onSelect = noop,
    getValueAtIndex,
    children,
    label = '',
    id,
    loading = false,
    placeholder = '',
    name,
    ...rest
}) => {
    const [expanded, setExpanded] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(null);
    const [selectedItem, setSelectedItem] = useState(value);

    const onHighlightIndex = (index) => {
        setHighlightedIndex(index);
    };

    const onInputValueChangeInternal = (newValue) => {
        setExpanded(true);
        setHighlightedIndex(null);
        setSelectedItem(null);
        onInputValueChange(newValue);
    };

    const onArrowDown = (currentIndex) => {
        const baseIndex = currentIndex === null ? -1 : currentIndex;
        setExpanded(true);
        setHighlightedIndex((baseIndex + 1) % itemCount);
    };

    const onArrowUp = (currentIndex) => {
        const baseIndex = currentIndex === null ? itemCount : currentIndex;
        const newIndex = baseIndex - 1;
        setExpanded(true);
        setHighlightedIndex(newIndex < 0 ? itemCount - 1 : newIndex);
    };

    const onEscape = () => {
        setExpanded(false);
    };

    const onSelectInternal = (newValue) => {
        if (!newValue) {
            return;
        }

        onSelect(newValue);
        setExpanded(false);
        setSelectedItem(newValue);
    };

    return (
        <ComboboxV2
            expanded={expanded}
            getValueAtIndex={getValueAtIndex}
            highlightedIndex={highlightedIndex}
            id={id}
            inputValue={value}
            label={label}
            loading={loading}
            name={name}
            onArrowDown={(...args) => onArrowDown(...args)}
            onArrowUp={(...args) => onArrowUp(...args)}
            onEscape={(...args) => onEscape(...args)}
            onFocus={(...args) => onFocus(...args)}
            onBlur={(...args) => onBlur(...args)}
            onHighlightIndex={(...args) => onHighlightIndex(...args)}
            onInputValueChange={(...args) =>
                onInputValueChangeInternal(...args)
            }
            onSelect={(...args) => onSelectInternal(...args)}
            placeholder={placeholder}
            selectedItem={selectedItem}
            {...rest}
        >
            {children}
        </ComboboxV2>
    );
};

export default StatefulComboboxV2;
